'use client'

import { useState } from 'react'

import { abToB64, b64ToAb } from '@/lib/utils/buffer-util'
import {
  confirmPasskeyLogin,
  confirmPasskeySetup,
  passkeyLogin,
  setupPasskey,
} from '@/service/user-profile.api'
import {
  ConfirmPasskeyLoginRequest,
  ConfirmPasskeySetupRequest,
  SetupPasskeyRequest,
} from '@/types/generated/common/auth'

// Must match the backend's [webauthn] rp_id (oems_v2 shared/config/configs):
// "localhost" against the dev backend, the bare apex "orderx.com" everywhere else
// so one credential works across every *.orderx.com subdomain. The login ceremony
// hashes this exact value into authenticatorData, so changing it invalidates every
// previously registered passkey.
function getPasskeyRpId(): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'localhost'
  }
  return 'orderx.com'
}

function getPasskeyRp(): PublicKeyCredentialRpEntity {
  return {
    name: 'OrderX',
    id: getPasskeyRpId(),
  }
}

const prepareRegistrationForServer = (
  credential: PublicKeyCredential,
  deviceName: string,
  sessionId: string
): ConfirmPasskeySetupRequest => {
  const response = credential.response as AuthenticatorAttestationResponse
  const publicKeyArrayBuffer = response.getPublicKey()
  if (!publicKeyArrayBuffer) {
    throw new Error('Failed to retrieve public key from credential response.')
  }

  return {
    credentialId: abToB64(credential.rawId),
    publicKey: abToB64(publicKeyArrayBuffer),
    attestationObject: abToB64(response.attestationObject),
    clientDataJson: abToB64(response.clientDataJSON),
    deviceName: deviceName,
    sessionId: sessionId,
  }
}

const prepareAssertionForServer = (
  credential: PublicKeyCredential,
  sessionId: string
): ConfirmPasskeyLoginRequest => {
  const response = credential.response as AuthenticatorAssertionResponse

  return {
    credentialId: abToB64(credential.rawId),
    authenticatorData: abToB64(response.authenticatorData),
    clientDataJson: abToB64(response.clientDataJSON),
    signature: abToB64(response.signature),
    sessionId: sessionId,
  }
}

/**
 * Extract OS and browser information from User Agent to generate a simple device name.
 * Example: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)..." -> "macOS Chrome"
 */
export function getDeviceNameFromUserAgent(): string {
  if (typeof window === 'undefined' || !navigator.userAgent) {
    return 'Unknown Device'
  }

  const ua = navigator.userAgent

  // Extract OS
  let os = 'Unknown OS'
  if (ua.includes('Mac OS X')) {
    os = 'macOS'
  } else if (ua.includes('Windows')) {
    os = 'Windows'
  } else if (ua.includes('Linux') && !ua.includes('Android')) {
    os = 'Linux'
  } else if (ua.includes('Android')) {
    os = 'Android'
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS'
  }

  // Extract browser
  let browser = 'Unknown Browser'
  if (ua.includes('Edg/')) {
    browser = 'Edge'
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    browser = 'Chrome'
  } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    browser = 'Safari'
  } else if (ua.includes('Firefox/')) {
    browser = 'Firefox'
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browser = 'Opera'
  }

  return `${os} ${browser}`
}

export function usePasskey() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = () => {
    setError(null)
  }

  const registerPasskey = async ({
    email,
    deviceName,
    stepUp = {},
  }: {
    email: string
    deviceName: string
    /** Password + one verification code: enrolling a passkey re-proves the account. */
    stepUp?: Partial<SetupPasskeyRequest>
  }) => {
    setIsLoading(true)
    setError(null)

    try {
      // Step 1: register passkey to server
      const setupResponse = await setupPasskey({ currentPassword: '', ...stepUp })

      if (!setupResponse.userId || !setupResponse.challenge || !setupResponse.sessionId) {
        throw new Error('Server did not return a challenge for registration.')
      }

      const publicKey: PublicKeyCredentialCreationOptions = {
        challenge: b64ToAb(setupResponse.challenge),
        rp: getPasskeyRp(),
        user: {
          // Password managers key passkeys on (rpId, user.id), so this must be the
          // stable account id — a varying value makes every registration look like a
          // new unknown account and duplicates pile up.
          id: new TextEncoder().encode(setupResponse.userId),
          name: email,
          displayName: email.split('@')[0],
        },
        pubKeyCredParams: [{ alg: -7, type: 'public-key' }], // ES256
        authenticatorSelection: {
          // The server refuses a credential whose authenticator did not verify the user:
          // a passkey stands in for both the password and the second factor.
          userVerification: 'required',
          residentKey: 'preferred',
        },
        excludeCredentials: setupResponse.existingCredentialIds.map((credentialId) => ({
          type: 'public-key' as const,
          id: b64ToAb(credentialId),
        })),
        attestation: 'none',
        timeout: 60000,
      }

      const credential = (await navigator.credentials.create({
        publicKey: publicKey,
      })) as PublicKeyCredential

      const confirmPayload = prepareRegistrationForServer(
        credential,
        deviceName,
        setupResponse.sessionId
      )
      const confirmResponse = await confirmPasskeySetup(confirmPayload)

      return confirmResponse
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Passkey login failed (WebAuthn or API error).'
      setError(errorMessage)
      return Promise.reject(e)
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithPasskey = async ({
    email,
    mediation = 'modal',
    abortSignal,
  }: {
    email?: string
    /**
     * 'modal' opens the browser's passkey dialog immediately (button click).
     * 'conditional' waits silently for the user to pick a passkey from the
     * email field's autofill — requires autocomplete="webauthn" on the input
     * and spec-mandates an empty allowCredentials list.
     */
    mediation?: 'modal' | 'conditional'
    abortSignal?: AbortSignal
  } = {}) => {
    setIsLoading(true)
    setError(null)

    try {
      const loginResponse = await passkeyLogin({ email })
      if (!loginResponse.challenge || !loginResponse.sessionId) {
        throw new Error('Server did not return a challenge for login.')
      }

      const allowCredentials =
        mediation === 'conditional' || !email
          ? undefined
          : loginResponse.credentialIds.map((credentialId) => ({
              type: 'public-key' as const,
              id: b64ToAb(credentialId),
            }))
      const challenge = loginResponse.challenge

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: b64ToAb(challenge),
        rpId: getPasskeyRpId(),
        allowCredentials: allowCredentials,
        userVerification: 'required',
      }

      const credential = (await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
        mediation: mediation === 'conditional' ? 'conditional' : 'required',
        signal: abortSignal,
      })) as PublicKeyCredential

      const confirmPayload = prepareAssertionForServer(credential, loginResponse.sessionId)
      const confirmResp = await confirmPasskeyLogin(confirmPayload)
      return confirmResp
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Passkey login failed (WebAuthn or API error).'
      setError(errorMessage)
      return Promise.reject(e)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    registerPasskey,
    loginWithPasskey,
    isLoading,
    error,
    clearError,
  }
}
